import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, Timestamp, doc, getDoc, getDocs, updateDoc, or, and, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingCart, Search, Eye, Plus, Trash2, RefreshCw, Share, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Order } from '../types';
import { processWooCommerceOrder, syncWooCommerceOrders, STATUS_MAPPING, deleteWooCommerceOrderFromFirebase, watchOrderStatusChanges } from '../services/woocommerce';
import { toast } from 'react-hot-toast';
import WooCommerceOrderSync from '../components/integrations/WooCommerceOrderSync';
import SyncOrdersModal, { SyncOptions } from '../components/orders/SyncOrdersModal';
import { calculateCommission } from '../utils/commissionUtils';

export const ORDER_STATUSES = {
  pending: 'Pendente',
  preparation: 'Em preparação',
  awaitingStock: 'Aguardar Stock',
  awaitingPickup: 'Aguardar recolha',
  shipped: 'Enviado',
  delivered: 'Entregue',
  cancelled: 'Cancelado',
  returned: 'Devolvido',
  awaiting_payment: 'Aguardando Pagamento'
} as const;

type BadgeVariant = 'warning' | 'info' | 'success' | 'error' | 'default';

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'current' | 'all' | 'delivered' | 'cancelled'>('current');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [integrations, setIntegrations] = useState<{ [key: string]: any }>({});
  const [userExemptions, setUserExemptions] = useState<{ [key: string]: boolean }>({});
  const [reloadingOrders, setReloadingOrders] = useState<string[]>([]);
  const [progress, setProgress] = useState<{
    total: number;
    current: number;
    message: string;
  }>({
    total: 0,
    current: 0,
    message: ''
  });
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser?.companyId) {
      loadOrders();
      // Carregar integrações
      const integrationsQuery = query(
        collection(db, 'integrations'),
        where('companyId', '==', currentUser.companyId),
        where('type', '==', 'woocommerce')
      );

      const unsubscribe = onSnapshot(integrationsQuery, (snapshot) => {
        const integrationsMap: { [key: string]: any } = {};
        snapshot.forEach((doc) => {
          const integration = doc.data();
          integrationsMap[doc.id] = integration;
        });
        setIntegrations(integrationsMap);
      });

      return () => unsubscribe();
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser?.companyId) {
      const usersRef = collection(db, 'users');
      const unsubscribe = onSnapshot(
        query(usersRef, where('companyId', '!=', '')),
        (snapshot) => {
          const exemptions: { [key: string]: boolean } = {};
          snapshot.forEach((doc) => {
            const userData = doc.data();
            exemptions[userData.companyId] = userData.commissionExempt || false;
          });
          setUserExemptions(exemptions);
        }
      );

      return () => unsubscribe();
    }
  }, [currentUser]);

  useEffect(() => {
    let unsubscribeWatcher: (() => void) | undefined;

    // Start watching for order status changes if we have a WooCommerce integration
    const setupWooCommerceSync = async () => {
      try {
        // Get WooCommerce integration
        const integrationsRef = collection(db, 'integrations');
        const q = query(
          integrationsRef,
          where('type', '==', 'woocommerce'),
          where('active', '==', true)
        );
        
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const integration = snapshot.docs[0];
          unsubscribeWatcher = watchOrderStatusChanges(integration.id);
        }
      } catch (error) {
        console.error('Error setting up WooCommerce sync:', error);
      }
    };

    setupWooCommerceSync();

    // Cleanup
    return () => {
      if (unsubscribeWatcher) {
        unsubscribeWatcher();
      }
    };
  }, []);

  const getBuyerName = async (data: Order): Promise<string> => {
    try {
      // Para vendas privadas, usar primeiro o endereço de entrega
      if (data.isPrivateSale && data.shippingAddress?.name) {
        return data.shippingAddress.name;
      }

      // Depois tentar dados do cliente no pedido
      if (data.customer?.name) {
        return data.customer.name;
      }

      // Para pedidos WooCommerce, usar informações de faturamento
      if (data.source === 'woocommerce' && data.sourceData?.billing) {
        const billing = data.sourceData.billing;
        return billing.first_name && billing.last_name ? 
          `${billing.first_name} ${billing.last_name}` :
          billing.first_name || billing.last_name || billing.company || 'Cliente não encontrado';
      }

      // Se não encontrou ainda, tentar buscar pelo buyerId
      if (data.buyerId) {
        const userDoc = await getDoc(doc(db, 'users', data.buyerId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          return userData.displayName || 
                 userData.name || 
                 userData.nomeCompleto || 
                 userData.email || 
                 'Cliente não encontrado';
        }
      }

      // Por fim, tentar obter informações da empresa do comprador
      if (data.buyerCompanyId) {
        const companyDoc = await getDoc(doc(db, 'companies', data.buyerCompanyId));
        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          return companyData.nomeComercial || 
                 companyData.commercialName || 
                 companyData.nomeEmpresa || 
                 companyData.legalName || 
                 'Cliente não encontrado';
        }
      }

      return 'Cliente não encontrado';
    } catch (err) {
      console.error('Erro ao carregar dados do comprador:', err);
      return 'Erro ao carregar cliente';
    }
  };

  const getSellerName = async (data: Order): Promise<string> => {
    const sellerId = data.sellerId || data.companyId;
    if (!sellerId) return 'N/A';

    try {
      const companyDoc = await getDoc(doc(db, 'companies', sellerId));
      if (companyDoc.exists()) {
        const companyData = companyDoc.data();
        return companyData.commercialName || companyData.legalName || 'N/A';
      }
    } catch (err) {
      console.error('Error loading seller company data:', err);
    }

    return 'N/A';
  };

  const getOrderCommission = async (order: Order) => {
    if (!order.sellerId && !order.companyId) return 0;
    
    const sellerId = order.sellerId || order.companyId;
    const isManualOrder = !order.isOnlineSale && !order.isPrivateSale && order.source !== 'woocommerce';
    
    try {
      const commission = await calculateCommission(
        order.total || 0,
        sellerId,
        isManualOrder
      );
      
      return commission.totalCommission;
    } catch (err) {
      console.error('Error calculating commission:', err);
      return 0;
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    setError('');

    try {
      if (!currentUser?.companyId) {
        setError('Usuário não tem uma empresa associada');
        return;
      }

      // Get all orders
      const ordersQuery = query(
        collection(db, 'orders'),
        or(
          // Pedidos onde a empresa é a vendedora direta
          where('sellerId', '==', currentUser.companyId),
          // Pedidos onde a empresa é a companyId
          where('companyId', '==', currentUser.companyId),
          // Pedidos do marketplace
          where('isOnlineSale', '==', true),
          // Pedidos do WooCommerce
          and(
            where('source', '==', 'woocommerce'),
            where('companyId', '==', currentUser.companyId)
          )
        )
      );

      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersPromises = ordersSnapshot.docs.map(async (doc) => {
        const data = doc.data() as Order;
        const buyerName = await getBuyerName(data);
        const sellerName = await getSellerName(data);
        const commission = await getOrderCommission(data);

        return {
          ...data,
          id: doc.id,
          buyerName,
          sellerName,
          commission,
          displayDate: formatDate(doc.data().createdAt)
        };
      });

      const ordersData = await Promise.all(ordersPromises);
      const relevantOrders = ordersData.filter(order => {
        // Direct company orders (including private sales)
        if (order.companyId === currentUser.companyId || order.sellerId === currentUser.companyId) {
          return true;
        }

        // Marketplace orders where at least one item belongs to the company
        if (order.items && Array.isArray(order.items)) {
          return order.items.some(item => item.companyId === currentUser.companyId);
        }

        return false;
      });

      // Sort orders by creation date, newest first
      const sortedOrders = relevantOrders.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });

      setOrders(sortedOrders);
    } catch (error: any) {
      console.error('Error loading orders:', error);
      setError(`Error loading orders: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | { seconds: number; nanoseconds: number } | undefined) => {
    if (!dateString) return '';
    
    let date: Date;
    
    // Se for um timestamp do Firestore
    if (typeof dateString === 'object' && 'seconds' in dateString) {
      date = new Date(dateString.seconds * 1000);
    } else if (typeof dateString === 'string') {
      date = new Date(dateString);
    } else {
      return '';
    }

    // Verificar se a data é válida
    if (isNaN(date.getTime())) {
      console.error('Data inválida:', dateString);
      return '';
    }

    return date.toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(',', '');
  };

  const getOrderStatusLabel = (status: string) => {
    return ORDER_STATUSES[status] || status;
  };

  const getOrderStatusVariant = (status: keyof typeof ORDER_STATUSES): BadgeVariant => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'preparation':
      case 'awaitingStock':
      case 'awaitingPickup':
        return 'info';
      case 'shipped':
      case 'delivered':
        return 'success';
      case 'cancelled':
      case 'returned':
        return 'error';
      default:
        return 'default';
    }
  };

  const getOrderType = (order: any) => {
    if (order.source === 'woocommerce') {
      return 'WooCommerce';
    }
    if (order.isMarketplace === true || order.isOnlineSale === true) {
      return 'Marketplace';
    }
    if (order.isPrivateSale === true) {
      return 'Venda Privada';
    }
    if (order.isManual === true && order.isMarketplace === false && order.isPrivateSale === false) {
      return 'Manual';
    }
    return 'Manual';
  };

  const getOrderTypeStyle = (order: any) => {
    if (order.source === 'woocommerce') {
      return 'bg-orange-100 text-orange-800';
    }
    if (order.isMarketplace === true || order.isOnlineSale === true) {
      return 'bg-blue-100 text-blue-800';
    }
    if (order.isPrivateSale === true) {
      return 'bg-purple-100 text-purple-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  const getOrderTypeVariant = (order: any): BadgeVariant => {
    if (order.source === 'woocommerce') return 'info';
    if (order.isMarketplace === true || order.isOnlineSale === true) return 'success';
    if (order.isPrivateSale === true) return 'warning';
    return 'default';
  };

  const getOrderTypeColor = (order: Order) => {
    if (order.source === 'woocommerce') return 'bg-orange-100 text-orange-800';
    if (order.isMarketplace === true || order.isOnlineSale === true) return 'bg-blue-100 text-blue-800';
    if (order.isPrivateSale === true) return 'bg-purple-100 text-purple-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getCommission = (order: Order) => {
    // Verificar isenção de comissões
    const isExempt = userExemptions[order.companyId] || false;
    if (isExempt) {
      return '€0.00';
    }

    // Não mostrar comissão para pedidos WooCommerce
    if (order.source === 'woocommerce') {
      return '';
    }

    // Para vendas do marketplace, usar a comissão salva
    if (order.isOnlineSale) {
      return order.commission ? `€${order.commission.toFixed(2)}` : '';
    }

    // Para vendas manuais, usar a comissão calculada ao criar o pedido
    if (!order.isOnlineSale && !order.source) {
      if (order.commission !== undefined && order.commission !== null) {
        return `€${order.commission.toFixed(2)}`;
      }
      // Se não houver comissão salva, calcular baseado no total sem IVA
      const total = order.total || 0;
      const totalWithoutVAT = total / 1.23; // Remover IVA de 23%
      const commission = totalWithoutVAT * 0.10; // 10% do valor sem IVA
      return `€${commission.toFixed(2)}`;
    }

    // Para vendas privadas
    if (order.isPrivateSale) {
      const total = order.total || 0;
      const totalWithoutVAT = total / 1.23; // Remover IVA de 23%
      const commission = totalWithoutVAT * 0.10; // 10% do valor sem IVA
      return `€${commission.toFixed(2)}`;
    }

    return '';
  };

  const formatCurrency = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined) return '€0.00';
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(numericValue) ? '€0.00' : `€${numericValue.toFixed(2)}`;
  };

  const formatOrderNumber = (order: any) => {
    if (order.source === 'woocommerce') {
      return `WC${order.sourceOrderId || order.orderId}`;
    }
    if (order.isOnlineSale) {
      return order.orderNumber;
    }
    if (order.isManual) {
      return order.orderNumber || order.orderId || '-';
    }
    return order.orderNumber || order.orderId || '-';
  };

  const getCustomerName = (order: any) => {
    // Para vendas privadas, usar o nome do endereço de entrega
    if (order.isPrivateSale) {
      return order.shippingAddress?.name || 'Cliente não encontrado';
    }

    // Para WooCommerce, manter a lógica original
    if (order.source === 'woocommerce') {
      const billing = order.sourceData?.billing;
      if (billing) {
        return billing.first_name && billing.last_name ? 
          `${billing.first_name} ${billing.last_name}` :
          billing.first_name || billing.last_name || billing.company || 'N/A';
      }
    }

    // Para marketplace e vendas manuais, manter a lógica original
    return order.isOnlineSale ? order.shippingAddress?.name : order.customer?.name;
  };

  const getOrderTotal = (order: any) => {
    if (order.source === 'woocommerce' && order.sourceData) {
      return parseFloat(order.sourceData.total || '0');
    }
    return order.total || 0;
  };

  const getFilteredOrders = () => {
    let filtered = orders;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(order =>
        order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    switch (statusFilter) {
      case 'current':
        filtered = filtered.filter(order => 
          !['shipped', 'cancelled', 'delivered', 'returned'].includes(order.status)
        );
        break;
      case 'delivered':
        filtered = filtered.filter(order => 
          order.status === 'delivered' || 
          order.status === 'completed'
        );
        break;
      case 'cancelled':
        filtered = filtered.filter(order => 
          order.status === 'cancelled'
        );
        break;
      // 'all' doesn't need filtering
    }

    // Sort by date, newest first
    return filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB.getTime() - dateA.getTime();
    });
  };

  const renderActions = (order: Order) => {
    const orderType = getOrderType(order);
    
    return (
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => navigate(`/dashboard/orders/${order.id}`)}
          className="text-primary hover:text-primary-dark"
          title="Ver detalhes"
        >
          <Eye size={20} />
        </button>
        {orderType === 'Manual' && (
          <button
            onClick={() => navigate(`/dashboard/orders/${order.id}/edit`)}
            className="text-primary hover:text-primary-dark"
            title="Editar pedido"
          >
            <Pencil size={20} />
          </button>
        )}
        {orderType === 'Manual' && (
          <button
            onClick={() => handleShare(order.id)}
            className="text-primary hover:text-primary-dark"
            title="Partilhar pedido"
          >
            <Share size={20} />
          </button>
        )}
        {order.woocommerce && (
          <button
            onClick={() => handleReimportOrder(order.id)}
            className={`text-primary hover:text-primary-dark ${
              reloadingOrders.includes(order.id) ? 'animate-spin' : ''
            }`}
            title="Recarregar do WooCommerce"
            disabled={reloadingOrders.includes(order.id)}
          >
            <RefreshCw size={20} />
          </button>
        )}
        {order.source === 'woocommerce' && (
          <button
            onClick={() => handleDeleteOrder(order.id)}
            className="text-red-600 hover:text-red-700"
            title="Apagar pedido"
            disabled={deleting === order.id}
          >
            <Trash2 size={20} />
          </button>
        )}
      </div>
    );
  };

  const handleReimportOrder = async (orderId: string) => {
    try {
      setReloadingOrders(prev => [...prev, orderId]);
      
      // Get the order from our database first
      const orderDoc = await getDoc(doc(db, 'orders', orderId));
      if (!orderDoc.exists()) {
        throw new Error('Encomenda não encontrada');
      }

      const orderData = orderDoc.data();
      // Try different possible locations of the WooCommerce ID
      const wooCommerceId = orderData.sourceOrderId || orderData.orderId || (orderData.sourceData && orderData.sourceData.id);
      
      if (!wooCommerceId) {
        throw new Error('ID do WooCommerce não encontrado');
      }

      // Get the integration details
      const integrationsQuery = query(
        collection(db, 'integrations'),
        where('companyId', '==', currentUser?.companyId),
        where('type', '==', 'woocommerce'),
        where('active', '==', true)
      );

      const snapshot = await getDocs(integrationsQuery);
      if (snapshot.empty) {
        throw new Error('Nenhuma integração WooCommerce ativa encontrada');
      }

      const integration = snapshot.docs[0].data();
      const { consumerKey, consumerSecret, url } = integration.config;

      // Get the order data from WooCommerce
      const response = await fetch(
        `${url}/wp-json/wc/v3/orders/${wooCommerceId}?consumer_key=${consumerKey}&consumer_secret=${consumerSecret}`
      );

      if (!response.ok) {
        throw new Error('Erro ao obter dados do WooCommerce');
      }

      const wooOrder = await response.json();
      
      // Process the order data
      const processedOrder = processWooCommerceOrder(wooOrder);
      
      // Update the order in Firestore with consistent ID storage
      await updateDoc(doc(db, 'orders', orderId), {
        sourceData: wooOrder,
        sourceOrderId: wooOrder.id.toString(), // Store it consistently
        orderId: wooOrder.id.toString(),       // Keep for backward compatibility
        items: processedOrder.items,
        total: processedOrder.total,
        subtotal: processedOrder.subtotal,
        status: processedOrder.status,
        updatedAt: new Date().toISOString()
      });
      
      toast.success('Encomenda reimportada com sucesso');
      
      // Refresh the orders list
      await loadOrders();
    } catch (error: any) {
      console.error('Error reimporting order:', error);
      toast.error(error.message || 'Erro ao reimportar encomenda');
    } finally {
      setReloadingOrders(prev => prev.filter(id => id !== orderId));
    }
  };

  const handleDeleteFromFirebase = async (orderId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation to order details
    
    if (window.confirm('Tem certeza que deseja apagar este pedido do Firebase? O pedido permanecerá no WooCommerce.')) {
      try {
        setDeleting(orderId);
        await deleteWooCommerceOrderFromFirebase(orderId);
        toast.success('Pedido apagado com sucesso');
        await loadOrders(); // Reload orders list
      } catch (error: any) {
        console.error('Erro ao apagar pedido:', error);
        toast.error(error.message || 'Erro ao apagar pedido');
      } finally {
        setDeleting(null);
      }
    }
  };

  const handleDeleteWooCommerceOrder = async (orderId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Previne a navegação para a página de detalhes
    
    if (window.confirm('Tem certeza que deseja apagar este pedido do WooCommerce? Ele será importado novamente na próxima sincronização.')) {
      try {
        setDeleting(orderId);
        // await deleteWooCommerceOrder(orderId);
        
        // Atualiza a lista localmente
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        setError('');
      } catch (err) {
        console.error('Erro ao apagar pedido:', err);
        setError('Erro ao apagar pedido. Por favor, tente novamente.');
      } finally {
        setDeleting(null);
      }
    }
  };

  const handleDeleteOrder = async (orderId: string) => {
    if (!confirm('Tem certeza que deseja apagar este pedido?')) {
      return;
    }

    setDeleting(orderId);
    try {
      await deleteWooCommerceOrderFromFirebase(orderId);
      toast.success('Pedido apagado com sucesso');
      loadOrders(); // Recarrega a lista de pedidos
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Erro ao apagar pedido');
    } finally {
      setDeleting(null);
    }
  };

  const handleSyncOrders = async (options: SyncOptions) => {
    setSyncing(true);
    setError('');
    setProgress({ total: 0, current: 0, message: 'Iniciando sincronização...' });
    
    try {
      if (!currentUser?.companyId) {
        setError('Usuário não tem uma empresa associada');
        return;
      }

      const integrationsQuery = query(
        collection(db, 'integrations'),
        where('companyId', '==', currentUser.companyId),
        where('type', '==', 'woocommerce')
      );
      const integrationsSnapshot = await getDocs(integrationsQuery);
      
      if (integrationsSnapshot.empty) {
        setError('Nenhuma integração WooCommerce encontrada. Configure a integração primeiro.');
        return;
      }

      for (const doc of integrationsSnapshot.docs) {
        const integration = doc.data();
        try {
          if (integration.config?.syncOrders) {
            await syncWooCommerceOrders(
              doc.id, 
              (progress) => {
                setProgress(progress);
              },
              options.syncType === 'fromDate' ? { fromDate: options.fromDate } : undefined,
              true // isWebhook = true para permitir a sincronização manual
            );
          }
        } catch (error: any) {
          console.error(`Erro ao sincronizar pedidos para integração ${doc.id}:`, error);
          setError(`Erro ao sincronizar pedidos: ${error.message}`);
          return;
        }
      }
      
      await loadOrders();
      toast.success('Pedidos sincronizados com sucesso');
    } catch (error: any) {
      console.error('Erro ao sincronizar pedidos:', error);
      setError(`Erro ao sincronizar pedidos: ${error.message}`);
    } finally {
      setSyncing(false);
      setProgress({ total: 0, current: 0, message: '' });
    }
  };

  const handleShare = async (orderId: string) => {
    const shareUrl = `${window.location.origin}/public/order/${orderId}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      // toast.success('Link copiado para a área de transferência!');
    } catch (err) {
      console.error('Failed to copy:', err);
      // toast.error('Erro ao copiar o link');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Pedidos</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsSyncModalOpen(true)}
            className="flex items-center gap-2 bg-[#0F172A] text-white px-6 py-2 rounded-lg hover:bg-[#1E293B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={syncing}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar Pedidos'}
          </button>
          <button
            onClick={() => navigate('/dashboard/orders/create')}
            className="flex items-center gap-2 bg-[#0F172A] text-white px-6 py-2 rounded-lg hover:bg-[#1E293B] transition-colors"
          >
            <Plus size={20} />
            Novo Pedido
          </button>
        </div>
      </div>

      {/* Status Filter and Search Bar */}
      <div className="flex gap-4 mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setStatusFilter('current')}
            className={`px-4 py-2 rounded-lg ${
              statusFilter === 'current'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Atuais
          </button>
          <button
            onClick={() => setStatusFilter('delivered')}
            className={`px-4 py-2 rounded-lg ${
              statusFilter === 'delivered'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Entregues
          </button>
          <button
            onClick={() => setStatusFilter('cancelled')}
            className={`px-4 py-2 rounded-lg ${
              statusFilter === 'cancelled'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Cancelados
          </button>
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 rounded-lg ${
              statusFilter === 'all'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Todos
          </button>
        </div>
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Pesquisar por número de encomenda ou cliente..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        </div>
      </div>

      {/* Progress bar */}
      {syncing && progress.total > 0 && (
        <div className="mb-4">
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium text-gray-700">
              {progress.message}
            </span>
            <span className="text-sm font-medium text-gray-700">
              {Math.round((progress.current / progress.total) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-primary h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-white shadow overflow-hidden rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nº ENCOMENDA
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                TIPO
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                CLIENTE
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                TOTAL
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                COMISSÃO
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ESTADO
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                DATA
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {getFilteredOrders().map((order: any) => (
              <tr key={`${order.id}_${order.sourceOrderId || ''}_${order.createdAt}`} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {formatOrderNumber(order)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getOrderTypeStyle(order)}`}>
                    {getOrderType(order)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {getCustomerName(order)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(getOrderTotal(order))}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(order.commission)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getOrderStatusVariant(order.status)}`}>
                    {getOrderStatusLabel(order.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {order.displayDate}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {renderActions(order)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {orders.length === 0 && (
        <div className="text-center py-12">
          <ShoppingCart className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma encomenda encontrada</h3>
          <p className="text-gray-500">
            Comece adicionando sua primeira encomenda clicando no botão "Inserir Encomenda".
          </p>
          <button
            onClick={() => navigate('/dashboard/orders/create')}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#C5D86D] text-white rounded-md hover:bg-[#b3c561]"
          >
            <Plus size={20} />
            Inserir Primeira Encomenda
          </button>
        </div>
      )}
      {/* Sync Modal */}
      <SyncOrdersModal
        isOpen={isSyncModalOpen}
        onClose={() => setIsSyncModalOpen(false)}
        onSync={handleSyncOrders}
      />
    </div>
  );
}